import React, { useMemo, useState, useSyncExternalStore } from 'react'
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import type { TariffRateAnalysisRow } from '../../../apis/types'
import { isPendingRatesetStatus } from './tariffHistoryUtils'
import { compareSeasonThenTou } from './tariffRateSort'

type SeriesPoint = { date: string } & Record<string, number | string | null>

const SEASON_LABELS: Record<string, string> = {
    SUMMER: 'Summer',
    WINTER: 'Winter',
    ALL: 'All seasons',
}

const TOU_LABELS: Record<string, string> = {
    PEAK: 'peak',
    MID_PEAK: 'mid-peak',
    PARTIAL_PEAK: 'partial-peak',
    OFF_PEAK: 'off-peak',
    SUPER_OFF_PEAK: 'super off-peak',
    ALL: 'all hours',
}

const SERIES_STROKE_LIGHT: Record<string, string> = {
    'SUMMER · PEAK': '#e11d48',
    'SUMMER · PARTIAL_PEAK': '#ea580c',
    'SUMMER · MID_PEAK': '#d97706',
    'SUMMER · OFF_PEAK': '#ca8a04',
    'SUMMER · SUPER_OFF_PEAK': '#a16207',
    'WINTER · PEAK': '#0284c7',
    'WINTER · PARTIAL_PEAK': '#2563eb',
    'WINTER · MID_PEAK': '#0369a1',
    'WINTER · OFF_PEAK': '#0f766e',
    'WINTER · SUPER_OFF_PEAK': '#7c3aed',
    'ALL · ALL': '#4f46e5',
}

const SERIES_STROKE_DARK: Record<string, string> = {
    'SUMMER · PEAK': '#fb7185',
    'SUMMER · PARTIAL_PEAK': '#fb923c',
    'SUMMER · MID_PEAK': '#fbbf24',
    'SUMMER · OFF_PEAK': '#facc15',
    'SUMMER · SUPER_OFF_PEAK': '#fde68a',
    'WINTER · PEAK': '#38bdf8',
    'WINTER · PARTIAL_PEAK': '#60a5fa',
    'WINTER · MID_PEAK': '#7dd3fc',
    'WINTER · OFF_PEAK': '#2dd4bf',
    'WINTER · SUPER_OFF_PEAK': '#c084fc',
    'ALL · ALL': '#818cf8',
}

const FALLBACK_LIGHT = ['#4f46e5', '#0f766e', '#be123c', '#0369a1', '#7c3aed', '#b45309']
const FALLBACK_DARK = ['#818cf8', '#2dd4bf', '#fb7185', '#38bdf8', '#c4b5fd', '#fbbf24']

type ChartChrome = {
    grid: string
    gridOpacity: number
    axis: string
    axisLine: string
    tooltipBg: string
    tooltipBorder: string
    tooltipText: string
    tooltipMuted: string
    cursor: string
}

const LIGHT_CHROME: ChartChrome = {
    grid: '#64748b',
    gridOpacity: 0.38,
    axis: '#64748b',
    axisLine: '#cbd5e1',
    tooltipBg: '#ffffff',
    tooltipBorder: '#e2e8f0',
    tooltipText: '#0f172a',
    tooltipMuted: '#64748b',
    cursor: '#94a3b8',
}

const DARK_CHROME: ChartChrome = {
    grid: '#94a3b8',
    gridOpacity: 0.2,
    axis: '#94a3b8',
    axisLine: '#334155',
    tooltipBg: '#1e293b',
    tooltipBorder: '#334155',
    tooltipText: '#f1f5f9',
    tooltipMuted: '#94a3b8',
    cursor: '#64748b',
}

export function seriesKey(row: TariffRateAnalysisRow): string {
    const base = `${row.season ?? 'ALL'} · ${row.touPeriod ?? 'ALL'}`
    if (row.vintageYear == null) return base
    return `${base} · ${row.vintageYear}`
}

export function parseSeriesKey(key: string): {
    season: string
    touPeriod: string
    vintageYear: string | null
} {
    const [season, touPeriod, vintageYear] = key.split(' · ')
    return {
        season: season ?? 'ALL',
        touPeriod: touPeriod ?? 'ALL',
        vintageYear: vintageYear ?? null,
    }
}

export function formatSeriesLabel(key: string): string {
    const { season, touPeriod, vintageYear } = parseSeriesKey(key)
    if (season === 'ALL' && touPeriod === 'ALL' && vintageYear == null) return 'All'
    const seasonLabel = SEASON_LABELS[season] ?? season
    const touLabel = TOU_LABELS[touPeriod] ?? touPeriod.toLowerCase().replaceAll('_', '-')
    const base = season === 'ALL' && touPeriod === 'ALL' ? 'All' : `${seasonLabel} ${touLabel}`
    return vintageYear != null ? `${base} (${vintageYear})` : base
}

export function formatChartDate(isoDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate)
    if (match == null) return isoDate
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

export function formatRateValue(value: number): string {
    return value.toFixed(6)
}

export function seriesStroke(key: string, dark: boolean): string {
    const map = dark ? SERIES_STROKE_DARK : SERIES_STROKE_LIGHT
    const mapped = map[key]
    if (mapped != null) return mapped
    const palette = dark ? FALLBACK_DARK : FALLBACK_LIGHT
    let hash = 0
    for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0
    }
    return palette[hash % palette.length] as string
}

function compareSeriesKeys(a: string, b: string): number {
    const left = parseSeriesKey(a)
    const right = parseSeriesKey(b)
    const seasonTou = compareSeasonThenTou(left, right)
    if (seasonTou !== 0) return seasonTou
    return (left.vintageYear ?? '').localeCompare(right.vintageYear ?? '')
}

/** One point per effective date so superseded ratesets stay on the trend. */
export function buildTariffRateTrendData(rows: TariffRateAnalysisRow[]): {
    data: SeriesPoint[]
    series: string[]
} {
    const keys = [...new Set(rows.map(seriesKey))].sort(compareSeriesKeys)
    const byDate = new Map<string, SeriesPoint>()
    for (const row of rows) {
        const date = row.rateEffectiveDate.slice(0, 10)
        const point = byDate.get(date) ?? { date }
        const key = seriesKey(row)
        const existing = point[key]
        if (typeof existing !== 'number' || row.totalRate >= existing) {
            point[key] = row.totalRate
            point[`${key}:status`] = row.ratesetStatus ?? 'ACTIVE'
        }
        byDate.set(date, point)
    }
    const data = [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)))
    return { data, series: keys }
}

type LineDotProps = {
    cx?: number
    cy?: number
    payload?: SeriesPoint
    dataKey?: string | number
}

function PendingRateDot({
    cx,
    cy,
    payload,
    dataKey,
    stroke,
    dark,
}: LineDotProps & { stroke: string; dark: boolean }): React.ReactElement | null {
    if (cx == null || cy == null || payload == null) return null
    const status = payload[`${String(dataKey)}:status`]
    if (typeof status !== 'string' || !isPendingRatesetStatus(status)) return null
    return (
        <circle
            cx={cx}
            cy={cy}
            r={3.5}
            fill={dark ? '#0f172a' : '#ffffff'}
            stroke={stroke}
            strokeWidth={2}
        />
    )
}

const darkThemeListeners = new Set<() => void>()
let darkThemeObserver: MutationObserver | null = null

function subscribeDarkTheme(onStoreChange: () => void): () => void {
    darkThemeListeners.add(onStoreChange)
    if (darkThemeObserver == null && typeof document !== 'undefined') {
        darkThemeObserver = new MutationObserver(() => {
            for (const listener of darkThemeListeners) listener()
        })
        darkThemeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        })
    }
    return () => {
        darkThemeListeners.delete(onStoreChange)
        if (darkThemeListeners.size === 0 && darkThemeObserver != null) {
            darkThemeObserver.disconnect()
            darkThemeObserver = null
        }
    }
}

function getDarkThemeSnapshot(): boolean {
    return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
}

function useIsDarkTheme(): boolean {
    return useSyncExternalStore(subscribeDarkTheme, getDarkThemeSnapshot, () => false)
}

type TrendTooltipItem = {
    dataKey?: string | number
    name?: string | number
    value?: number | string
    color?: string
    payload?: SeriesPoint
}

const TrendTooltip: React.FC<{
    active?: boolean
    label?: string | number
    payload?: TrendTooltipItem[]
    chrome: ChartChrome
}> = ({ active, label, payload, chrome }) => {
    if (active !== true || payload == null || payload.length === 0) return null
    const items = payload.filter((item) => typeof item.value === 'number')
    if (items.length === 0) return null
    return (
        <div
            className="rounded-md px-3 py-2 text-xs shadow-md"
            style={{
                backgroundColor: chrome.tooltipBg,
                border: `1px solid ${chrome.tooltipBorder}`,
                color: chrome.tooltipText,
            }}
        >
            <div className="mb-1.5 font-medium" style={{ color: chrome.tooltipMuted }}>
                {formatChartDate(String(label ?? ''))}
            </div>
            <ul className="space-y-1">
                {items.map((item) => {
                    const key = String(item.dataKey ?? item.name ?? '')
                    const point = item.payload
                    const status = point?.[`${key}:status`]
                    const pending = typeof status === 'string' && isPendingRatesetStatus(status)
                    return (
                        <li key={key} className="flex items-center gap-2">
                            <span
                                className="inline-block h-2 w-2 shrink-0 rounded-full"
                                style={{ backgroundColor: item.color }}
                            />
                            <span className="min-w-0 flex-1 truncate">{formatSeriesLabel(key)}</span>
                            <span className="tabular-nums">{formatRateValue(Number(item.value))}</span>
                            {pending ? (
                                <span className="text-[10px] uppercase tracking-wide" style={{ color: chrome.tooltipMuted }}>
                                    pending
                                </span>
                            ) : null}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export const TariffRateTrendChart: React.FC<{
    rows: TariffRateAnalysisRow[]
    /** Drop the outer card when the chart already sits inside a Panel. */
    embedded?: boolean
}> = ({ rows, embedded = false }) => {
    const { data, series } = useMemo(() => buildTariffRateTrendData(rows), [rows])
    const dark = useIsDarkTheme()
    const chrome = dark ? DARK_CHROME : LIGHT_CHROME
    const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set())
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)

    const pendingBySeries = useMemo(() => {
        const pending = new Set<string>()
        for (const key of series) {
            const isPending = data.some((point) => {
                const status = point[`${key}:status`]
                return typeof status === 'string' && isPendingRatesetStatus(status)
            })
            if (isPending) pending.add(key)
        }
        return pending
    }, [data, series])

    if (data.length === 0) return null

    const hasPending = pendingBySeries.size > 0

    return (
        <div
            className={
                embedded
                    ? undefined
                    : 'rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900'
            }
        >
            <div className="mb-3 flex items-baseline justify-between gap-3">
                <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">Total over time</h3>
                <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">$/kWh</span>
            </div>
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid
                            stroke={chrome.grid}
                            strokeOpacity={chrome.gridOpacity}
                            strokeDasharray="3 6"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatChartDate}
                            minTickGap={28}
                            tick={{ fontSize: 11, fill: chrome.axis }}
                            stroke={chrome.axisLine}
                            tickLine={false}
                            axisLine={{ stroke: chrome.axisLine }}
                            dy={4}
                        />
                        <YAxis
                            domain={[
                                (min: number) => Math.max(0, Math.floor((min - 0.02) * 20) / 20),
                                (max: number) => Math.ceil((max + 0.02) * 20) / 20,
                            ]}
                            tickFormatter={(value: number) => value.toFixed(3)}
                            width={48}
                            tick={{ fontSize: 11, fill: chrome.axis }}
                            stroke={chrome.axisLine}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            content={<TrendTooltip chrome={chrome} />}
                            cursor={{ stroke: chrome.cursor, strokeDasharray: '4 4', strokeWidth: 1 }}
                            isAnimationActive={false}
                        />
                        {series.map((key) => {
                            const muted = hoveredKey != null && hoveredKey !== key
                            const stroke = seriesStroke(key, dark)
                            return (
                                <Line
                                    key={key}
                                    type="linear"
                                    dataKey={key}
                                    name={formatSeriesLabel(key)}
                                    stroke={stroke}
                                    strokeWidth={hoveredKey === key ? 2.5 : 2}
                                    strokeOpacity={muted ? 0.18 : 1}
                                    dot={(props) => (
                                        <PendingRateDot
                                            cx={props.cx}
                                            cy={props.cy}
                                            payload={props.payload}
                                            dataKey={
                                                typeof props.dataKey === 'string' || typeof props.dataKey === 'number'
                                                    ? props.dataKey
                                                    : undefined
                                            }
                                            stroke={stroke}
                                            dark={dark}
                                        />
                                    )}
                                    activeDot={{ r: 4, strokeWidth: 2, fill: dark ? '#0f172a' : '#ffffff', stroke }}
                                    connectNulls
                                    hide={hidden.has(key)}
                                    isAnimationActive={false}
                                />
                            )
                        })}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-1 gap-y-1">
                {series.map((key) => {
                    const stroke = seriesStroke(key, dark)
                    const isHidden = hidden.has(key)
                    return (
                        <li key={key}>
                            <button
                                type="button"
                                aria-pressed={!isHidden}
                                className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-200 dark:hover:bg-slate-800 ${
                                    isHidden ? 'opacity-40' : ''
                                }`}
                                onClick={() => {
                                    setHidden((current) => {
                                        const next = new Set(current)
                                        if (next.has(key)) next.delete(key)
                                        else next.add(key)
                                        return next
                                    })
                                }}
                                onMouseEnter={() => setHoveredKey(key)}
                                onMouseLeave={() => setHoveredKey(null)}
                                onFocus={() => setHoveredKey(key)}
                                onBlur={() => setHoveredKey(null)}
                            >
                                <span
                                    className="inline-block h-0.5 w-3.5 shrink-0 rounded-full"
                                    style={{
                                        backgroundColor: isHidden ? 'transparent' : stroke,
                                        boxShadow: isHidden ? `inset 0 0 0 1px ${stroke}` : undefined,
                                    }}
                                />
                                {formatSeriesLabel(key)}
                            </button>
                        </li>
                    )
                })}
            </ul>
            {hasPending ? (
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    Hollow markers are pending ratesets.
                </p>
            ) : null}
        </div>
    )
}

export default TariffRateTrendChart
